import * as React from "react";
import { Check, Minus, X } from "lucide-react";

import { PollOptionTally, PollResults, PollVoteChoice } from "@/api/types";
import { cn } from "@/lib/utils";

/**
 * The host's results grid: respondents down, proposed times across.
 *
 * <p>Columns stay <b>chronological</b> and carry a rank badge, rather than re-sorting themselves by
 * popularity — a grid whose columns reshuffle every time a vote lands is unreadable. The toggle
 * switches to ranked order for the host who wants it.</p>
 *
 * <p>Cells are pills with an icon, never colour alone. Rank 1 is drawn as an outline, never a fill:
 * it is a suggestion. 5 Yes / 5 No outranks 4 Yes / 6 Less-preferred here, and which of those a host
 * actually wants is not something a comparator can know, so all three counts stay on screen.</p>
 */

const DAY = new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "short" });
const TIME = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });

function labelFor(t: { start_at: string; end_at: string }): string {
  return `${DAY.format(new Date(t.start_at))} ${TIME.format(new Date(t.start_at))}`;
}

const CHOICE_LABEL: Record<PollVoteChoice, string> = {
  YES: "Yes",
  LESS_PREFERRED: "Less preferred",
  NO: "No",
};

function Cell({ choice }: { choice: PollVoteChoice | undefined }) {
  if (!choice) {
    // Not "No" — this person was never asked, or skipped it. Shown as an en dash, not a cross.
    return <span className="text-sm text-muted-foreground">{"–"}</span>;
  }
  const style =
    choice === "YES"
      ? "bg-brand-teal/20 text-foreground"
      : choice === "LESS_PREFERRED"
        ? "bg-brand-yellow/30 text-foreground"
        : "bg-brand-coral/20 text-foreground";
  const Icon = choice === "YES" ? Check : choice === "LESS_PREFERRED" ? Minus : X;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-sm font-medium",
        style,
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {CHOICE_LABEL[choice]}
    </span>
  );
}

export function PollResultsGrid({
  results,
  confirmedOptionId,
  onChoose,
}: {
  results: PollResults;
  confirmedOptionId: string | null;
  /** Absent once a time is confirmed: the grid becomes the record of how it was decided. */
  onChoose?: (optionId: string) => void;
}) {
  const [order, setOrder] = React.useState<"time" | "rank">("time");
  const columns: PollOptionTally[] =
    order === "rank"
      ? [...results.options].sort((a, b) => a.rank - b.rank)
      : [...results.options].sort((a, b) => Date.parse(a.start_at) - Date.parse(b.start_at));

  if (results.respondent_count === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-foreground">
        Nobody has answered yet. Share the link and the answers land here.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs font-bold uppercase tracking-wide text-foreground">
          {results.respondent_count} {results.respondent_count === 1 ? "response" : "responses"}
        </h3>
        <div className="inline-flex overflow-hidden rounded-lg border border-border text-xs font-semibold">
          {(["time", "rank"] as const).map((o) => (
            <button
              key={o}
              type="button"
              aria-pressed={order === o}
              onClick={() => setOrder(o)}
              className={cn(
                "px-2.5 py-1 transition",
                order === o
                  ? "bg-brand-blue/10 text-brand-blue"
                  : "text-foreground hover:bg-muted",
              )}
            >
              {o === "time" ? "By time" : "By popularity"}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[36rem] border-collapse text-left">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-card px-2 py-2 text-xs font-bold uppercase tracking-wide text-foreground">
                Who
              </th>
              {columns.map((c) => (
                <th key={c.option_id} className="px-2 py-2 align-bottom">
                  <div
                    className={cn(
                      "space-y-1 rounded-lg px-2 py-1.5",
                      // An outline, never a fill: rank 1 is a suggestion the host may overrule.
                      c.rank === 1 && "border border-brand-blue/50",
                      c.option_id === confirmedOptionId && "bg-brand-teal/15",
                    )}
                  >
                    <div className="text-sm font-semibold text-foreground">{labelFor(c)}</div>
                    <div className="text-xs font-medium text-foreground">
                      #{c.rank}
                      {c.option_id === confirmedOptionId && " · confirmed"}
                    </div>
                    {onChoose && (
                      <button
                        type="button"
                        onClick={() => onChoose(c.option_id)}
                        className="rounded-full border border-foreground px-2 py-0.5 text-xs font-semibold text-foreground transition hover:bg-muted"
                      >
                        Choose
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results.respondents.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <th
                  scope="row"
                  className="sticky left-0 z-10 bg-card px-2 py-2 text-sm font-medium text-foreground"
                >
                  <div>{r.name}</div>
                  <div className="text-sm text-muted-foreground">{r.email}</div>
                </th>
                {columns.map((c) => (
                  <td
                    key={c.option_id}
                    className="px-2 py-2"
                    aria-label={`${r.name}: ${
                      r.votes[c.option_id] ? CHOICE_LABEL[r.votes[c.option_id]] : "No answer"
                    }, ${labelFor(c)}`}
                  >
                    <Cell choice={r.votes[c.option_id]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-foreground/30">
              <th
                scope="row"
                className="sticky left-0 z-10 bg-card px-2 py-2 text-xs font-bold uppercase tracking-wide text-foreground"
              >
                Totals
              </th>
              {columns.map((c) => (
                <td key={c.option_id} className="px-2 py-2 text-sm text-foreground">
                  <div>{c.yes} yes</div>
                  <div>{c.less_preferred} less preferred</div>
                  <div>{c.no} no</div>
                  {c.no_response > 0 && <div>{c.no_response} no answer</div>}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
